import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button, Field, Input, Select } from '@/ui/primitives';
import { useRegistrarAbordagem } from '../../api';
import {
  ABORDAGEM_CANAIS, ABORDAGEM_RESULTADOS, ABORDAGEM_ACOES,
  type Detail,
} from '../../schemas';

interface Props {
  id: string;
  detail: Detail | undefined;
  cardId: string;
}

function formatDate(s: string | null | undefined) {
  if (!s) return null;
  try { return new Date(s).toLocaleString('pt-BR'); } catch { return s; }
}

// Fase Abordagem — registro estruturado de tentativas de contato comercial.
// Pré-preenche o canal com o `canal_preferido` do perfil. Lista histórico de
// abordagens do card. Reseta campos de evento ao trocar de card.
export function FaseAbordagem({ id, detail, cardId }: Props) {
  const registrarAbordagem = useRegistrarAbordagem(id);
  const perfilSalvo = detail?.perfil ?? null;

  const [abCanal, setAbCanal] = useState('');
  const [abQuemAtendeu, setAbQuemAtendeu] = useState('');
  const [abResultado, setAbResultado] = useState('');
  const [abAnotacoes, setAbAnotacoes] = useState('');
  const [abProximaAcao, setAbProximaAcao] = useState('');
  const [abProximoContato, setAbProximoContato] = useState('');

  // Pré-preenche o canal com o preferido do cliente quando o perfil carrega.
  useEffect(() => {
    if (perfilSalvo?.canal_preferido && !abCanal) {
      setAbCanal(String(perfilSalvo.canal_preferido));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfilSalvo?.cliente_cpf]);

  // Reseta campos de evento quando troca de card (canal preferido fica).
  useEffect(() => {
    setAbResultado('');
    setAbQuemAtendeu('');
    setAbAnotacoes('');
    setAbProximaAcao('');
    setAbProximoContato('');
  }, [cardId]);

  function onSubmitAbordagem() {
    if (!abResultado) return;
    registrarAbordagem.mutate(
      {
        ...(abCanal && { canal: abCanal }),
        ...(abQuemAtendeu.trim() && { quem_atendeu: abQuemAtendeu.trim() }),
        resultado: abResultado,
        ...(abAnotacoes.trim() && { anotacoes: abAnotacoes.trim() }),
        ...(abProximaAcao && { proxima_acao: abProximaAcao }),
        ...(abProximoContato && { proximo_contato_em: abProximoContato }),
      },
      {
        onSuccess: () => {
          setAbResultado('');
          setAbQuemAtendeu('');
          setAbAnotacoes('');
          setAbProximaAcao('');
          setAbProximoContato('');
        },
      },
    );
  }

  const abordagensAnteriores = (detail?.eventos ?? [])
    .filter((e) => e.tipo === 'abordagem')
    .slice(0, 8);

  return (
    <section className="border border-border rounded-lg p-4 bg-elevated flex flex-col gap-4">
      <div>
        <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted">
          Registrar abordagem
        </h3>
        <p className="text-[0.78rem] text-text-2 mt-1">
          Cada ligação, mensagem ou visita vira um registro. A próxima ação define se a oportunidade avança, espera ou encerra.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Canal usado" htmlFor="crm-ab-canal">
          <Select id="crm-ab-canal" value={abCanal} onChange={(e) => setAbCanal(e.target.value)} className="text-[0.82rem]">
            <option value="">—</option>
            {ABORDAGEM_CANAIS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </Select>
        </Field>
        <Field label="Quem atendeu" htmlFor="crm-ab-quem">
          <Input id="crm-ab-quem" value={abQuemAtendeu} onChange={(e) => setAbQuemAtendeu(e.target.value)} placeholder="ex: filho do produtor" />
        </Field>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2">
          Resultado da tentativa *
        </span>
        <div className="flex flex-col gap-1.5">
          {ABORDAGEM_RESULTADOS.map((r) => {
            const active = abResultado === r.v;
            const dotClass =
              r.tone === 'ok' ? 'bg-[var(--ok)]'
              : r.tone === 'info' ? 'bg-[var(--info)]'
              : r.tone === 'danger' ? 'bg-[var(--danger)]'
              : r.tone === 'warn' ? 'bg-[var(--warning)]'
              : 'bg-[var(--muted)]';
            return (
              <button
                key={r.v}
                type="button"
                onClick={() => setAbResultado(r.v)}
                className={cn(
                  'flex items-center gap-2.5 rounded-[10px] px-3 py-2 text-left text-[0.82rem]',
                  active
                    ? 'bg-[var(--nav-active-bg)] font-semibold text-text'
                    : 'bg-elevated border border-border font-medium text-text-2 hover:border-border2',
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[1.5px]',
                    active ? 'border-accent' : 'border-[var(--border2)]',
                  )}
                >
                  {active && <span className="h-2 w-2 rounded-full bg-accent" />}
                </span>
                <span className={cn('h-2 w-2 shrink-0 rounded-full', dotClass)} />
                <span>{r.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <Field label="Anotações da conversa" htmlFor="crm-ab-anot">
        <textarea
          id="crm-ab-anot" value={abAnotacoes} onChange={(e) => setAbAnotacoes(e.target.value)} rows={3}
          placeholder="O que foi falado, sinais de interesse, dúvidas levantadas, prazo dado pelo cliente."
          className="w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text placeholder:text-text-muted hover:border-[var(--text-muted)] focus:border-accent focus:outline-none focus:shadow-[0_0_0_3px_rgba(233,106,69,0.15)] resize-none"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Próxima ação" htmlFor="crm-ab-acao">
          <Select id="crm-ab-acao" value={abProximaAcao} onChange={(e) => setAbProximaAcao(e.target.value)} className="text-[0.82rem]">
            <option value="">—</option>
            {ABORDAGEM_ACOES.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
          </Select>
        </Field>
        <Field label="Próximo contato em" htmlFor="crm-ab-prox">
          <Input id="crm-ab-prox" type="datetime-local" value={abProximoContato} onChange={(e) => setAbProximoContato(e.target.value)} />
        </Field>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="primary" size="sm" onClick={onSubmitAbordagem} disabled={registrarAbordagem.isPending || !abResultado}>
          {registrarAbordagem.isPending ? 'Registrando…' : 'Registrar abordagem'}
        </Button>
        <span className="text-[0.78rem] text-text-muted">
          {abProximaAcao === 'enviar_proposta'
            ? 'Avança para Proposta.'
            : abProximaAcao === 'abandonar'
            ? 'Fecha como perdido (anotações viram motivo).'
            : 'Fica em Abordagem aguardando próximo contato.'}
        </span>
      </div>

      {abordagensAnteriores.length > 0 && (
        <div className="border-t border-border pt-4 mt-2 flex flex-col gap-2">
          <h4 className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2">
            Tentativas anteriores ({abordagensAnteriores.length})
          </h4>
          <ul className="flex flex-col gap-2">
            {abordagensAnteriores.map((e) => {
              const p = (e.payload as Record<string, unknown> | null) ?? {};
              const res = ABORDAGEM_RESULTADOS.find((r) => r.v === p.resultado);
              return (
                <li key={e.id} className="border border-border rounded-md px-3 py-2 text-[0.78rem] flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-text">
                      {res?.label ?? String(p.resultado ?? '—')}
                    </span>
                    <span className="text-text-muted">{formatDate(e.created_at)}</span>
                  </div>
                  {Boolean(p.canal) && (
                    <span className="text-text-2">canal: {String(p.canal)}{p.quem_atendeu ? ` · com ${String(p.quem_atendeu)}` : ''}</span>
                  )}
                  {Boolean(p.anotacoes) && (
                    <p className="text-text-2">{String(p.anotacoes)}</p>
                  )}
                  {Boolean(p.proxima_acao) && (
                    <span className="text-text-muted">
                      próxima ação: {ABORDAGEM_ACOES.find((a) => a.v === p.proxima_acao)?.label ?? String(p.proxima_acao)}
                      {p.proximo_contato_em ? ` em ${formatDate(String(p.proximo_contato_em))}` : ''}
                    </span>
                  )}
                  {e.ator_nome && (
                    <span className="text-text-muted">por {e.ator_nome}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
